import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  Circle,
  Clock,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  User,
  Calendar,
  AlertCircle,
  Flag,
  FileText,
  Eye,
  TrendingUp,
  X
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { format, isPast, isToday, isTomorrow, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';

interface Task {
  id: string;
  title: string;
  description?: string;
  due_date: string;
  completed: boolean;
  priority: string;
  type: string;
  student_id?: string;
  students?: {
    first_name: string;
    last_name: string;
    email: string;
  };
  created_at: string;
}

const TasksPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // New states for adding task
  const [showAddModal, setShowAddModal] = useState(false);
  const [students, setStudents] = useState<{ id: string; first_name: string; last_name: string }[]>([]);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    due_date: format(new Date(), 'yyyy-MM-dd'),
    priority: 'medium',
    student_id: ''
  });

  useEffect(() => {
    fetchTasks();
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const { data } = await supabase
        .from('students')
        .select('id, first_name, last_name')
        .eq('branch_id', user?.branchId)
        .order('first_name')
        .limit(100);

      setStudents(data || []);
    } catch (error) {
      console.error('Öğrenciler yüklenirken hata:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('tasks')
        .select(`
          *,
          students (first_name, last_name, email)
        `)
        .eq('branch_id', user?.branchId)
        .order('due_date', { ascending: true });

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error('Görevler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTask = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        title: newTask.title,
        description: newTask.description,
        due_date: new Date(newTask.due_date).toISOString(),
        priority: newTask.priority,
        student_id: newTask.student_id ? newTask.student_id : null,
        created_by: user?.id,
        branch_id: user?.branchId
      };

      if (editingTask) {
        const { error } = await supabase
          .from('tasks')
          .update({
            title: payload.title,
            description: payload.description,
            due_date: payload.due_date,
            priority: payload.priority,
            student_id: payload.student_id
          })
          .eq('id', editingTask.id);

        if (error) throw error;
      } else {
        const { error } = await supabase.from('tasks').insert(payload);
        if (error) throw error;
      }

      setShowAddModal(false);
      setEditingTask(null);
      setNewTask({
        title: '',
        description: '',
        due_date: format(new Date(), 'yyyy-MM-dd'),
        priority: 'medium',
        student_id: ''
      });
      fetchTasks();
    } catch (error) {
      console.error('Görev kaydedilirken hata:', error);
      alert('Görev işleminde hata oluştu.');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!await window.appConfirm('Bu görevi silmek istediğinize emin misiniz?')) return;

    try {
      const { error } = await supabase.from('tasks').delete().eq('id', taskId);
      if (error) throw error;

      if (selectedTask?.id === taskId) setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      console.error('Silme hatası:', error);
      alert('Görev silinemedi.');
    }
  };

  const openEditModal = (task: Task) => {
    setEditingTask(task);
    setNewTask({
      title: task.title,
      description: task.description || '',
      due_date: format(new Date(task.due_date), 'yyyy-MM-dd'),
      priority: task.priority,
      student_id: task.student_id || ''
    });
    setShowAddModal(true);
  };

  const toggleTaskComplete = async (taskId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ completed: !currentStatus })
        .eq('id', taskId);

      if (error) throw error;
      fetchTasks();
    } catch (error) {
      console.error('Görev güncellenirken hata:', error);
    }
  };

  const getPriorityConfig = (priority: string) => {
    const configs: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
      high: {
        label: 'Yüksek',
        color: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-100 dark:border-red-800',
        icon: <Flag className="w-3 h-3 text-red-600 dark:text-red-400" />
      },
      medium: {
        label: 'Orta',
        color: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 border border-yellow-100 dark:border-yellow-800',
        icon: <Flag className="w-3 h-3 text-yellow-600 dark:text-yellow-400" />
      },
      low: {
        label: 'Düşük',
        color: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 border border-green-100 dark:border-green-800',
        icon: <Flag className="w-3 h-3 text-green-600 dark:text-green-400" />
      }
    };
    return configs[priority] || configs.medium;
  };

  const getDateLabel = (dateString: string) => {
    const date = parseISO(dateString);
    if (isToday(date)) return 'Bugün';
    if (isTomorrow(date)) return 'Yarın';
    if (isPast(date)) return 'Gecikmiş';
    return format(date, 'dd MMM yyyy', { locale: tr });
  };

  const getDateColor = (dateString: string) => {
    const date = parseISO(dateString);
    if (isPast(date)) return 'text-red-600 dark:text-red-400 font-semibold';
    if (isToday(date)) return 'text-orange-600 dark:text-orange-400 font-semibold';
    return 'text-gray-600 dark:text-gray-400';
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch =
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (task.students && `${task.students.first_name} ${task.students.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesPriority = filterPriority === 'all' || task.priority === filterPriority;
    const matchesStatus =
      filterStatus === 'all' ||
      (filterStatus === 'completed' && task.completed) ||
      (filterStatus === 'pending' && !task.completed);

    return matchesSearch && matchesPriority && matchesStatus;
  });

  const pendingTasks = filteredTasks.filter(t => !t.completed);
  const completedTasks = filteredTasks.filter(t => t.completed);
  const overdueTasks = pendingTasks.filter(t => isPast(parseISO(t.due_date)));
  const todayTasks = pendingTasks.filter(t => isToday(parseISO(t.due_date)));

  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => !t.completed).length,
    completed: tasks.filter(t => t.completed).length,
    overdue: tasks.filter(t => !t.completed && isPast(parseISO(t.due_date))).length,
    today: tasks.filter(t => !t.completed && isToday(parseISO(t.due_date))).length
  };

  const completionRate = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="w-16 h-16 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-500 font-medium animate-pulse">Görevler yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-gray-900 dark:text-white tracking-tight">
            Görevlerim
          </h1>
          <p className="text-lg text-gray-500 dark:text-gray-400 mt-2 font-medium">
            {stats.pending} bekleyen, {stats.completed} tamamlanmış görev
          </p>
        </div>
        <button
          onClick={() => {
            setEditingTask(null);
            setNewTask({
              title: '',
              description: '',
              due_date: format(new Date(), 'yyyy-MM-dd'),
              priority: 'medium',
              student_id: ''
            });
            setShowAddModal(true);
          }}
          className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl font-semibold shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 transition-all duration-300 transform hover:-translate-y-0.5">
          <div className="p-1.5 bg-white/20 rounded-lg group-hover:scale-110 transition-transform">
            <Plus className="w-5 h-5" />
          </div>
          <span>Yeni Görev</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl shadow-gray-200/50 dark:shadow-black/20 border border-gray-100 dark:border-gray-700 group hover:scale-[1.02] transition-transform">
          <div className="flex items-center gap-4 mb-3">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-2xl text-blue-600 dark:text-blue-400">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Toplam</p>
              <p className="text-2xl font-black text-gray-900 dark:text-white">{stats.total}</p>
            </div>
          </div>
          <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full" style={{ width: '100%' }}></div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl shadow-gray-200/50 dark:shadow-black/20 border border-gray-100 dark:border-gray-700 group hover:scale-[1.02] transition-transform">
          <div className="flex items-center gap-4 mb-3">
            <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-2xl text-orange-600 dark:text-orange-400">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Bekleyen</p>
              <p className="text-2xl font-black text-gray-900 dark:text-white">{stats.pending}</p>
            </div>
          </div>
          <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
            <div className="h-full bg-orange-500 rounded-full" style={{ width: `${(stats.pending / stats.total) * 100}%` }}></div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl shadow-gray-200/50 dark:shadow-black/20 border border-gray-100 dark:border-gray-700 group hover:scale-[1.02] transition-transform">
          <div className="flex items-center gap-4 mb-3">
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-2xl text-green-600 dark:text-green-400">
              <CheckCircle className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Tamamlanan</p>
              <p className="text-2xl font-black text-gray-900 dark:text-white">{stats.completed}</p>
            </div>
          </div>
          <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
            <div className="h-full bg-green-500 rounded-full" style={{ width: `${completionRate}%` }}></div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl shadow-gray-200/50 dark:shadow-black/20 border border-gray-100 dark:border-gray-700 group hover:scale-[1.02] transition-transform">
          <div className="flex items-center gap-4 mb-3">
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-2xl text-red-600 dark:text-red-400">
              <AlertCircle className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Gecikmiş</p>
              <p className="text-2xl font-black text-gray-900 dark:text-white">{stats.overdue}</p>
            </div>
          </div>
          <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
            <div className="h-full bg-red-500 rounded-full" style={{ width: `${(stats.overdue / stats.total) * 100}%` }}></div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl shadow-gray-200/50 dark:shadow-black/20 border border-gray-100 dark:border-gray-700 group hover:scale-[1.02] transition-transform">
          <div className="flex items-center gap-4 mb-3">
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-2xl text-purple-600 dark:text-purple-400">
              <Calendar className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Bugün</p>
              <p className="text-2xl font-black text-gray-900 dark:text-white">{stats.today}</p>
            </div>
          </div>
          <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
            <div className="h-full bg-purple-500 rounded-full" style={{ width: `${(stats.today / stats.total) * 100}%` }}></div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-2 shadow-xl shadow-gray-200/50 dark:shadow-black/20 border border-gray-100 dark:border-gray-700 flex flex-col lg:flex-row gap-2">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-gray-400">
            <Search className="w-5 h-5" />
          </div>
          <input
            type="text"
            placeholder="Görev, açıklama veya öğrenci ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3.5 bg-transparent border-none focus:ring-0 text-gray-900 dark:text-white placeholder-gray-400 font-medium"
          />
        </div>
        <div className="h-auto w-px bg-gray-200 dark:bg-gray-700 mx-2 hidden lg:block" />
        <div className="flex gap-2 p-1">
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border-none rounded-2xl text-gray-700 dark:text-gray-200 font-medium focus:ring-2 focus:ring-blue-500/20 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <option value="all">Tüm Öncelikler</option>
            <option value="high">Yüksek</option>
            <option value="medium">Orta</option>
            <option value="low">Düşük</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border-none rounded-2xl text-gray-700 dark:text-gray-200 font-medium focus:ring-2 focus:ring-blue-500/20 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <option value="all">Tüm Durumlar</option>
            <option value="pending">Bekleyen</option>
            <option value="completed">Tamamlanan</option>
          </select>
        </div>
      </div>

      {/* Tasks Content */}
      <div className="space-y-8">
        {overdueTasks.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 ml-2">
              <div className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]"></div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Gecikmiş Görevler ({overdueTasks.length})</h3>
            </div>
            <div className="grid gap-3">
              {overdueTasks.map(task => (
                <TaskItem
                  key={task.id}
                  task={task}
                  onToggle={() => toggleTaskComplete(task.id, task.completed)}
                  onView={() => setSelectedTask(task)}
                  onEdit={() => openEditModal(task)}
                  onDelete={() => handleDeleteTask(task.id)}
                  getPriorityConfig={getPriorityConfig}
                  getDateColor={getDateColor}
                  getDateLabel={getDateLabel}
                />
              ))}
            </div>
          </div>
        )}

        {todayTasks.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 ml-2">
              <div className="w-2 h-2 rounded-full bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.6)]"></div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Bugün ({todayTasks.length})</h3>
            </div>
            <div className="grid gap-3">
              {todayTasks.map(task => (
                <TaskItem
                  key={task.id}
                  task={task}
                  onToggle={() => toggleTaskComplete(task.id, task.completed)}
                  onView={() => setSelectedTask(task)}
                  onEdit={() => openEditModal(task)}
                  onDelete={() => handleDeleteTask(task.id)}
                  getPriorityConfig={getPriorityConfig}
                  getDateColor={getDateColor}
                  getDateLabel={getDateLabel}
                />
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center gap-3 ml-2">
            <div className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)]"></div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              Yaklaşan Görevler ({pendingTasks.filter(t => !isPast(parseISO(t.due_date)) && !isToday(parseISO(t.due_date))).length})
            </h3>
          </div>
          <div className="grid gap-3">
            {pendingTasks
              .filter(t => !isPast(parseISO(t.due_date)) && !isToday(parseISO(t.due_date)))
              .map(task => (
                <TaskItem
                  key={task.id}
                  task={task}
                  onToggle={() => toggleTaskComplete(task.id, task.completed)}
                  onView={() => setSelectedTask(task)}
                  onEdit={() => openEditModal(task)}
                  onDelete={() => handleDeleteTask(task.id)}
                  getPriorityConfig={getPriorityConfig}
                  getDateColor={getDateColor}
                  getDateLabel={getDateLabel}
                />
              ))}
          </div>
        </div>

        {completedTasks.length > 0 && (
          <div className="space-y-4 pt-8 border-t border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3 ml-2">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <h3 className="text-xl font-bold text-gray-500 dark:text-gray-400">Tamamlanan Görevler ({completedTasks.length})</h3>
            </div>
            <div className="grid gap-3 opacity-60 hover:opacity-100 transition-opacity">
              {completedTasks.slice(0, 5).map(task => (
                <TaskItem
                  key={task.id}
                  task={task}
                  onToggle={() => toggleTaskComplete(task.id, task.completed)}
                  onView={() => setSelectedTask(task)}
                  onEdit={() => openEditModal(task)}
                  onDelete={() => handleDeleteTask(task.id)}
                  getPriorityConfig={getPriorityConfig}
                  getDateColor={getDateColor}
                  getDateLabel={getDateLabel}
                />
              ))}
            </div>
          </div>
        )}

        {filteredTasks.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-12 text-center border border-dashed border-gray-300 dark:border-gray-700">
            <div className="w-20 h-20 bg-gray-50 dark:bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Görev Bulunamadı</h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              Henüz bu kriterlere uygun bir görev bulunmuyor.
            </p>
          </div>
        )}
      </div>

      {/* Add Task Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setShowAddModal(false)} />
          <div className="relative bg-white dark:bg-gray-900 rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/50">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingTask ? 'Görevi Düzenle' : 'Yeni Görev Ekle'}
              </h2>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSaveTask} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Başlık</label>
                <input
                  type="text"
                  required
                  value={newTask.title}
                  onChange={e => setNewTask({ ...newTask, title: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  placeholder="Görev başlığı"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Açıklama</label>
                <textarea
                  value={newTask.description}
                  onChange={e => setNewTask({ ...newTask, description: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all h-24 resize-none"
                  placeholder="Görev detayları..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Son Tarih</label>
                  <input
                    type="date"
                    required
                    value={newTask.due_date}
                    onChange={e => setNewTask({ ...newTask, due_date: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Öncelik</label>
                  <select
                    value={newTask.priority}
                    onChange={e => setNewTask({ ...newTask, priority: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  >
                    <option value="low">Düşük</option>
                    <option value="medium">Orta</option>
                    <option value="high">Yüksek</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">İlgili Öğrenci (Opsiyonel)</label>
                <select
                  value={newTask.student_id}
                  onChange={e => setNewTask({ ...newTask, student_id: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                >
                  <option value="">Seçiniz...</option>
                  {students.map(student => (
                    <option key={student.id} value={student.id}>
                      {student.first_name} {student.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-500/20 transition-all"
                >
                  {editingTask ? 'Güncelle' : 'Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task Modal (View Details) */}
      {selectedTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
            onClick={() => setSelectedTask(null)}
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col">
            <div className="sticky top-0 z-10 flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-t-3xl">
              <div className="flex items-center gap-3">
                {selectedTask.completed ? (
                  <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-full text-green-600">
                    <CheckCircle className="w-6 h-6" />
                  </div>
                ) : (
                  <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-full text-orange-600">
                    <Clock className="w-6 h-6" />
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedTask.title}
                  </h2>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${getPriorityConfig(selectedTask.priority).color}`}>
                      {getPriorityConfig(selectedTask.priority).label}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedTask(null)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-8 space-y-6">
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 uppercase tracking-wider">Açıklama</h3>
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl text-gray-700 dark:text-gray-300">
                  {selectedTask.description || 'Açıklama bulunmuyor.'}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 border border-gray-100 dark:border-gray-800 rounded-2xl flex items-center gap-4">
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-blue-600">
                    <Calendar className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-bold uppercase">Bitiş Tarihi</p>
                    <p className={`text-base font-bold ${getDateColor(selectedTask.due_date)}`}>
                      {getDateLabel(selectedTask.due_date)}
                    </p>
                  </div>
                </div>

                {selectedTask.students && (
                  <div className="p-4 border border-gray-100 dark:border-gray-800 rounded-2xl flex items-center gap-4">
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl text-purple-600">
                      <User className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-bold uppercase">Öğrenci</p>
                      <p className="text-base font-bold text-gray-900 dark:text-white">
                        {selectedTask.students.first_name} {selectedTask.students.last_name}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setSelectedTask(null); // Close view modal
                    openEditModal(selectedTask); // Open edit modal
                  }}
                  className="flex-1 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-2xl font-bold hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors flex items-center justify-center gap-2"
                >
                  <Edit className="w-5 h-5" />
                  Düzenle
                </button>
                <button
                  onClick={() => handleDeleteTask(selectedTask.id)}
                  className="flex-1 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-2xl font-bold hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-5 h-5" />
                  Sil
                </button>
              </div>

              <div className="flex gap-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                <button
                  onClick={() => setSelectedTask(null)}
                  className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-2xl font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  Kapat
                </button>
                <button
                  onClick={() => {
                    toggleTaskComplete(selectedTask.id, selectedTask.completed);
                    setSelectedTask(null);
                  }}
                  className={`flex-[2] px-6 py-3 text-white rounded-2xl font-bold transition-shadow shadow-lg active:scale-95 ${selectedTask.completed
                    ? 'bg-orange-500 hover:bg-orange-600 shadow-orange-500/20'
                    : 'bg-green-600 hover:bg-green-700 shadow-green-500/20'
                    }`}
                >
                  {selectedTask.completed ? 'Yeniden Aç' : 'Görevi Tamamla'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Extracted for cleaner code
const TaskItem: React.FC<{
  task: Task;
  onToggle: () => void;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  getPriorityConfig: (p: string) => any;
  getDateColor: (d: string) => string;
  getDateLabel: (d: string) => string;
}> = ({ task, onToggle, onView, onEdit, onDelete, getPriorityConfig, getDateColor, getDateLabel }) => (
  <div className="group flex items-start gap-4 p-5 bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-lg transition-all duration-300">
    <button
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className="mt-1 flex-shrink-0 text-gray-400 hover:text-green-500 transition-colors"
    >
      {task.completed ? (
        <CheckCircle className="w-6 h-6 text-green-500" />
      ) : (
        <Circle className="w-6 h-6 hover:fill-blue-50" />
      )}
    </button>

    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-3 mb-1">
        <h4 className={`text-lg font-bold truncate ${task.completed ? 'line-through text-gray-400 dark:text-gray-600' : 'text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors'}`}>
          {task.title}
        </h4>
        <span className={`flex-shrink-0 px-2.5 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wide ${getPriorityConfig(task.priority).color}`}>
          {getPriorityConfig(task.priority).label}
        </span>
      </div>

      {task.description && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 line-clamp-1">{task.description}</p>
      )}

      <div className="flex items-center gap-4 text-xs font-medium">
        <div className={`flex items-center gap-1.5 ${getDateColor(task.due_date)}`}>
          <Calendar className="w-4 h-4" />
          <span>{getDateLabel(task.due_date)}</span>
        </div>

        {task.students && (
          <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
            <User className="w-4 h-4" />
            <span>{task.students.first_name} {task.students.last_name}</span>
          </div>
        )}
      </div>
    </div>

    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        onClick={(e) => { e.stopPropagation(); onView(); }}
        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-xl transition-colors"
      >
        <Eye className="w-5 h-5" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
      >
        <Edit className="w-5 h-5" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
      >
        <Trash2 className="w-5 h-5" />
      </button>
    </div>
  </div>
);

export default TasksPage;
